import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { Icon, IconButton, Paragraph, Title } from "@sicredi/react";
import {
  formatValueMoney,
  toastErrorMenssage,
} from "#/contextProver/ContextProverServices";

interface InputRangeProps extends React.InputHTMLAttributes<HTMLInputElement> {
  min: number;
  max: number;
  value: number;
  label: string;
  valueFormat: string;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  setValueFormat: React.Dispatch<React.SetStateAction<string>>;
}

export const InputRange: React.FC<InputRangeProps> = ({
  min,
  max,
  value,
  label,
  valueFormat,
  setValue,
  setValueFormat,
  ...rest
}) => {
  const [sliderRange, setSliderRange] = useState(Number(value));

  const [isFocus, setIsFocus] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);

  function handleSliderInput() {
    if (sliderRef.current) {
      if (max === min) {
        setSliderRange(100);
        return;
      }

      const range = max - min;
      if (value && isFocus) {
        const distance = value - min;
        const percentage = (distance / range) * 100;
        setSliderRange(percentage);
      } else {
        const valueAsNumber = Number(sliderRef.current.value);
        const distance = valueAsNumber - min;
        const percentage = (distance / range) * 100;
        setSliderRange(percentage);
      }
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const novoValor = event.target.value;
    setValue(Number(novoValor));
    setValueFormat(formatValueMoney(Number(novoValor)));
  };

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      Number(event.target.value.replace(/[^0-9]/g, "").replace(",.", ""))
    );
  };

  const handleOnBlur = () => {
    setIsFocus(false);
    if (value < min) {
      setValue(min);
      setValueFormat(formatValueMoney(min));
      toastErrorMenssage("O valor digitado, tem que ser maior que o valor Min");
    } else if (value > max) {
      setValue(max);
      setValueFormat(formatValueMoney(max));
      toastErrorMenssage("O valor digitado, tem que ser menor que o valor Máx");
    } else {
      setValueFormat(formatValueMoney(value));
    }
  };

  useEffect(() => {
    handleSliderInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderRef, value, valueFormat]);

  return (
    <div className={styles.range_slider}>
      <Title as="h4" size="medium" className={styles.title}>
        {label}
      </Title>

      <div className={styles.value_icon}>
        {isFocus && (
          <input
            value={value}
            type="text"
            className={styles.value_input}
            onChange={handleChangeInput}
            autoFocus={isFocus}
            onBlur={handleOnBlur}
            onInput={() => handleSliderInput()}
            ref={sliderRef}
          />
        )}
        {!isFocus && (
          <>
            <Title
              onClick={() => setIsFocus(true)}
              className={styles.value}
              as="h2"
            >
              {valueFormat}
            </Title>
            <Icon
              onClick={() => setIsFocus(true)}
              className={styles.icon}
              name="edit"
              appearance="primary"
            />
          </>
        )}
      </div>

      <div className={styles.min_max}>
        <Paragraph size="small" weight="normal">
          Min
        </Paragraph>
        <Paragraph size="small" weight="normal">
          Máx
        </Paragraph>
      </div>
      {!isFocus && (
        <div className={styles.slider_container}>
          <input
            value={value}
            type="range"
            className={styles.slider}
            onInput={() => handleSliderInput()}
            min={min}
            max={max}
            step={"0.01"}
            onChange={handleChange}
            ref={sliderRef}
          />
          <div
            className={styles.slider_thumb}
            style={{
              left: `calc(${sliderRange}% - 0.9em)`,
            }}
          >
            <IconButton
              label="Slider"
              name="chevron-left-right"
              notificationBadgeCounter={0}
              size="small"
              tip=""
            />
          </div>
          <div
            className={styles.progress}
            style={{
              width: `${sliderRange}%`,
            }}
          ></div>
        </div>
      )}
      <div className={styles.min_max}>
        <Paragraph size="small" weight="normal">
          {formatValueMoney(min)}
        </Paragraph>
        <Paragraph size="small" weight="normal">
          {formatValueMoney(max)}
        </Paragraph>
      </div>
    </div>
  );
};
